import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getBytes,
  getDownloadURL,
  getStorage,
  listAll,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { doc, getDoc, setDoc } from "firebase/firestore";
import {
  ChevronDown,
  ChevronUp,
  Headphones,
  Loader2,
  Mic2,
  Plus,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { firestore } from "../helpers/firebaseConfig";
import { generateGeminiTtsAudio } from "../helpers/geminiTts";
import "./AudioTestPage.css";

const STORAGE_FOLDER = "audio-testing";
const AUDIO_REFRESH_INTERVAL = 15000;

const MALE_VOICES = [
  "Achird", "Algenib", "Algieba", "Alnilam", "Charon", "Enceladus",
  "Fenrir", "Iapetus", "Orus", "Puck", "Rasalgethi", "Sadachbia",
  "Sadaltager", "Schedar", "Umbriel", "Zubenelgenubi",
];

const FEMALE_VOICES = [
  "Achernar", "Aoede", "Autonoe", "Callirrhoe", "Despina", "Erinome",
  "Gacrux", "Kore", "Laomedeia", "Leda", "Pulcherrima", "Sulafat",
  "Vindemiatrix", "Zephyr",
];

const VOICE_GROUPS = [
  { label: "Male voices", voices: MALE_VOICES },
  { label: "Female voices", voices: FEMALE_VOICES },
];

function VoiceSelect({ id, label, value, onChange }) {
  return (
    <label className="audio-test-field" htmlFor={id}>
      <span>{label}</span>
      <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
        {VOICE_GROUPS.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.voices.map((voice) => <option key={voice} value={voice}>{voice}</option>)}
          </optgroup>
        ))}
      </select>
    </label>
  );
}

function formatTimestamp(id) {
  const timestamp = Number(id);
  if (!Number.isFinite(timestamp)) return id;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}

function AudioTestPage() {
  const storage = useMemo(() => getStorage(), []);
  const [title, setTitle] = useState("");
  const [script, setScript] = useState("");
  const [alexVoice, setAlexVoice] = useState("Autonoe");
  const [jordanVoice, setJordanVoice] = useState("Puck");
  const [tests, setTests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [generatingIds, setGeneratingIds] = useState({});
  const [generationErrors, setGenerationErrors] = useState({});
  const [expandedIds, setExpandedIds] = useState({});
  const [error, setError] = useState("");

  const findAudioUrl = useCallback(async (id, isLegacy = false) => {
    const path = isLegacy
      ? `${STORAGE_FOLDER}/${id}.wav`
      : `${STORAGE_FOLDER}/${id}/${id}.wav`;
    try {
      return await getDownloadURL(storageRef(storage, path));
    } catch (audioError) {
      if (audioError?.code === "storage/object-not-found") return "";
      throw audioError;
    }
  }, [storage]);

  const loadTests = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setIsLoading(true);
    setError("");

    try {
      const result = await listAll(storageRef(storage, STORAGE_FOLDER));
      const legacyRefs = result.items
        .filter((item) => item.name.endsWith(".txt"))
        .map((item) => ({ item, isLegacy: true }));
      const nestedRefs = (await Promise.all(result.prefixes.map(async (prefix) => {
        const nestedResult = await listAll(prefix);
        return nestedResult.items
          .filter((item) => item.name === `${prefix.name}.txt`)
          .map((item) => ({ item, isLegacy: false }));
      }))).flat();
      const scriptRefs = [...nestedRefs, ...legacyRefs];
      const loadedTests = await Promise.all(scriptRefs.map(async ({ item, isLegacy }) => {
        const id = item.name.slice(0, -4);
        const [bytes, voiceDocument, audioUrl] = await Promise.all([
          getBytes(item),
          getDoc(doc(firestore, STORAGE_FOLDER, id)),
          findAudioUrl(id, isLegacy),
        ]);
        const voices = voiceDocument.data()?.voices || {};

        return {
          id,
          title: voiceDocument.data()?.title || id,
          script: new TextDecoder().decode(bytes),
          alexVoice: voices.Alex || "Autonoe",
          jordanVoice: voices.Jordan || "Puck",
          audioUrl,
          isLegacy,
        };
      }));

      loadedTests.sort((a, b) => Number(b.id) - Number(a.id));
      setTests(loadedTests);
    } catch (loadError) {
      console.error("Unable to load audio tests", loadError);
      setError("We couldn't load the audio tests. Please try again.");
    } finally {
      if (!quiet) setIsLoading(false);
    }
  }, [findAudioUrl, storage]);

  useEffect(() => {
    loadTests();
    const interval = window.setInterval(() => loadTests({ quiet: true }), AUDIO_REFRESH_INTERVAL);
    return () => window.clearInterval(interval);
  }, [loadTests]);

  const createTest = async (event) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedScript = script.trim();
    if (!trimmedTitle || !trimmedScript || isSaving) return;

    setIsSaving(true);
    setError("");
    const id = Date.now().toString();

    try {
      const blob = new Blob([trimmedScript], { type: "text/plain;charset=utf-8" });
      await uploadBytes(storageRef(storage, `${STORAGE_FOLDER}/${id}/${id}.txt`), blob, {
        contentType: "text/plain;charset=utf-8",
      });
      await setDoc(doc(firestore, STORAGE_FOLDER, id), {
        title: trimmedTitle,
        voices: {
          Alex: alexVoice,
          Jordan: jordanVoice,
        },
      });

      setTests((current) => [{
        id,
        title: trimmedTitle,
        script: trimmedScript,
        alexVoice,
        jordanVoice,
        audioUrl: "",
        isLegacy: false,
      }, ...current]);
      setTitle("");
      setScript("");
    } catch (saveError) {
      console.error("Unable to save audio test", saveError);
      setError("The script couldn't be saved. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const generateAudio = async (test) => {
    if (generatingIds[test.id]) return;

    setGeneratingIds((current) => ({ ...current, [test.id]: true }));
    setGenerationErrors((current) => ({ ...current, [test.id]: "" }));

    const bucket = storage.app.options.storageBucket;
    const basePath = `${STORAGE_FOLDER}/${test.id}`;
    const result = await generateGeminiTtsAudio(
      `gs://${bucket}/${basePath}/${test.id}.txt`,
      `gs://${bucket}/${basePath}/${test.id}.wav`,
      test.alexVoice,
      test.jordanVoice
    );

    if (!result) {
      setGenerationErrors((current) => ({
        ...current,
        [test.id]: "Audio generation could not be started. Please try again.",
      }));
    } else {
      window.setTimeout(() => loadTests({ quiet: true }), 2000);
    }

    setGeneratingIds((current) => ({ ...current, [test.id]: false }));
  };

  return (
    <main className="audio-test-page">
      <div className="audio-test-shell">
        <header className="audio-test-header">
          <div className="audio-test-icon"><Headphones size={28} /></div>
          <div>
            <p className="audio-test-eyebrow">Gemini TTS workspace</p>
            <h1>Audio tests</h1>
            <p>Create scripts, assign voices, and review generated audio.</p>
          </div>
        </header>

        <form className="audio-test-form" onSubmit={createTest}>
          <div className="audio-test-form-heading">
            <div>
              <h2>New audio test</h2>
              <p>Your script will be saved to cloud storage with a timestamp ID.</p>
            </div>
            <Sparkles size={22} aria-hidden="true" />
          </div>

          <label className="audio-test-field audio-test-title-field" htmlFor="audio-title">
            <span>Title</span>
            <input
              id="audio-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="e.g. Marineview evacuation walkthrough"
              required
            />
          </label>

          <label className="audio-test-field audio-test-script" htmlFor="audio-script">
            <span>Script</span>
            <textarea
              id="audio-script"
              value={script}
              onChange={(event) => setScript(event.target.value)}
              placeholder={"Alex: Welcome to the audio test.\nJordan: Let's hear how it sounds."}
              rows={8}
            />
          </label>

          <div className="audio-test-voices">
            <VoiceSelect id="alex-voice" label="Alex voice" value={alexVoice} onChange={setAlexVoice} />
            <VoiceSelect id="jordan-voice" label="Jordan voice" value={jordanVoice} onChange={setJordanVoice} />
          </div>

          <button
            className="audio-test-create"
            type="submit"
            disabled={!title.trim() || !script.trim() || isSaving}
          >
            {isSaving ? <Loader2 className="audio-test-spin" size={18} /> : <Plus size={18} />}
            {isSaving ? "Saving..." : "Create audio test"}
          </button>
        </form>

        {error && <div className="audio-test-error" role="alert">{error}</div>}

        <section className="audio-test-list-section">
          <div className="audio-test-list-heading">
            <div>
              <h2>Saved tests</h2>
              <span>{tests.length} {tests.length === 1 ? "test" : "tests"}</span>
            </div>
            <button type="button" onClick={() => loadTests()} disabled={isLoading} aria-label="Refresh audio tests">
              <RefreshCw className={isLoading ? "audio-test-spin" : ""} size={17} />
              Refresh
            </button>
          </div>

          {isLoading ? (
            <div className="audio-test-empty"><Loader2 className="audio-test-spin" size={24} />Loading audio tests...</div>
          ) : tests.length === 0 ? (
            <div className="audio-test-empty">
              <Mic2 size={28} />
              <strong>No audio tests yet</strong>
              <span>Add a script above to create your first one.</span>
            </div>
          ) : (
            <div className="audio-test-list">
              {tests.map((test) => (
                <article className="audio-test-card" key={test.id}>
                  <div className="audio-test-card-top">
                    <div>
                      <h3>{test.title}</h3>
                      <div className="audio-test-card-meta">
                        <span className="audio-test-id">#{test.id}</span>
                        <time dateTime={new Date(Number(test.id)).toISOString()}>{formatTimestamp(test.id)}</time>
                      </div>
                    </div>
                    <div className={`audio-test-status ${test.audioUrl ? "is-ready" : ""}`}>
                      <span />
                      {test.audioUrl ? "Audio ready" : "Awaiting audio"}
                    </div>
                  </div>

                  <div className="audio-test-script-preview">
                    <p className={`audio-test-card-script ${expandedIds[test.id] ? "is-expanded" : ""}`}>
                      {test.script}
                    </p>
                    <button
                      className="audio-test-expand"
                      type="button"
                      onClick={() => setExpandedIds((current) => ({
                        ...current,
                        [test.id]: !current[test.id],
                      }))}
                      aria-expanded={Boolean(expandedIds[test.id])}
                    >
                      {expandedIds[test.id] ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                      {expandedIds[test.id] ? "Show less" : "Show script"}
                    </button>
                  </div>

                  <div className="audio-test-card-footer">
                    <div className="audio-test-card-voices">
                      <span>Alex · {test.alexVoice}</span>
                      <span>Jordan · {test.jordanVoice}</span>
                    </div>
                    <button
                      className="audio-test-generate"
                      type="button"
                      onClick={() => generateAudio(test)}
                      disabled={generatingIds[test.id] || test.isLegacy}
                      title={test.isLegacy ? "Recreate this test to generate audio using the new folder format." : undefined}
                    >
                      {generatingIds[test.id]
                        ? <Loader2 className="audio-test-spin" size={16} />
                        : <Sparkles size={16} />}
                      {generatingIds[test.id] ? "Submitting..." : "Generate audio"}
                    </button>
                  </div>

                  {generationErrors[test.id] && (
                    <p className="audio-test-generation-error" role="alert">
                      {generationErrors[test.id]}
                    </p>
                  )}

                  {test.audioUrl && (
                    <audio className="audio-test-player" controls preload="metadata" src={test.audioUrl}>
                      Your browser does not support audio playback.
                    </audio>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

export default AudioTestPage;
